import React, {useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import ChooseSaveWizardContent
  from 'components/organisms/WizardContent/ChooseSaveWizardContent/ChooseSaveWizardContent';
import StyledSaveSearchWizard from 'components/organisms/Wizards/SaveSearchWizard/SaveSearchWizard.styles';
import constants from 'helpers/constants';
import utils from 'helpers/utils';
import {useAuthorize} from 'components/organisms/Providers/AuthProvider/AuthProvider';

const SaveSearchWizard = React.forwardRef((props, ref) => {
  const {
    tableProvider,
    saveTypes,
    onSubmit,
    onChange,
    ...innerProps
  } = useComponentProps(props, 'SaveSearchWizard');

  const innerRef = useRef(null);

  const [internalState, setInternalState] = useState({
    type: null,
    collectionType: constants.collection.createTypes.create
  });

  useImperativeHandle(ref, () => innerRef.current);

  const authorize = useAuthorize();

  const steps = useMemo(() => {
    const steps = [];

    steps.push({
      name: 'type',
      label: 'Choose save type',
      shortLabel: 'Save',
      Content: (props) => {
        return <ChooseSaveWizardContent ref={props.ref}
                                        className={props.className}
                                        wizard={props.wizard}
                                        step={props.step}
                                        tableProvider={tableProvider}
                                        saveTypes={saveTypes}
                                        onComplete={props.onComplete}
                                        onError={props.onError}
                                        onDirty={props.onDirty}/>
      }
    });

    if (Boolean(tableProvider.view?.userView) && [constants.query.saveTypes.view].includes(internalState.type)) {
      return steps;
    } else {
      const fields = [];
      if ([constants.query.saveTypes.collection, constants.query.saveTypes.source].includes(internalState.type)) {
        const asSource = internalState.type === constants.query.saveTypes.source;

        if (asSource) {
          fields.push({
            name: 'sourceName',
            label: 'Source name',
            entity: 'saveSearch',
            type: constants.formFieldTypes.text,
            required: true
          });
        }

        fields.push({
          name: 'collectionType',
          label: 'Collection type',
          inlineLabel: 'this field',
          type: constants.formFieldTypes.list,
          validation: constants.formFieldValidationTypes.text,
          conversion: constants.formFieldConversionTypes.value,
          options: 'collectionCreateTypes',
          entity: 'saveSearch',
          required: true,
          FormFieldProps: {
            hiddenLabel: true,
            size: 'smaller',
            variant: 'staticLabel',
            ListProps: {
              catchFocus: false,
              orientation: 'horizontal',
              gap: 16
            }
          }
        });

        fields.push({
          name: 'collection',
          label: 'Collection',
          placeholder: 'Search for a collection',
          entity: 'saveSearch',
          type: constants.formFieldTypes.autocomplete,
          conversion: constants.formFieldValidationTypes.none,
          validation: constants.formFieldValidationTypes.collection,
          relation: `collectionType(${constants.collection.createTypes.existing})`,
          options: 'collections',
          required: true,
          FormFieldProps: {
            multiple: false,
            fullWidth: true
          }
        });

        fields.push({
          name: 'collectionName',
          label: 'Collection name',
          entity: 'saveSearch',
          type: constants.formFieldTypes.text,
          relation: `collectionType(${constants.collection.createTypes.create})`,
          required: true
        });

        fields.push({
          name: 'collectionVisibility',
          entity: 'saveSearch',
          label: 'Collection privacy',
          type: constants.formFieldTypes.autocomplete,
          conversion: constants.formFieldConversionTypes.value,
          relation: `collectionType(${constants.collection.createTypes.create})`,
          initial: constants.collection.visibility.client,
          options: constants.data.collectionVisibility
            .filter((v) => authorize({
              attribute: `collection.field.visibility.update.${v.value}`
            })),
          required: true
        });
      } else if ([constants.query.saveTypes.view].includes(internalState.type)) {
        fields.push({
          name: 'viewName',
          label: 'View name',
          entity: 'saveSearch',
          type: constants.formFieldTypes.text,
          required: true
        });
      }

      steps.push({
        name: 'settings',
        label: 'Settings',
        shortLabel: 'Settings',
        fields: fields,
        fieldData: tableProvider.fieldData
      });

      return steps;
    }
  }, [saveTypes, internalState.type, tableProvider, authorize]);

  const handleChange = (isPrevEnabled, isNextEnabled, isSubmitEnabled, isSubmitting) => {
    onChange?.(isPrevEnabled, isNextEnabled, isSubmitEnabled, isSubmitting);

    const data = innerRef.current?.data?.[innerRef.current?.dataKey];
    setInternalState(utils.updater({
      type: data?.type?.value
    }, true));
  }

  const handleSubmit = (data, actions, onSuccess, onError) => {
    return onSubmit?.(data.search, actions, onSuccess, onError);
  }

  const initialData = useMemo(() => ({
    'search': {
      collectionType: constants.collection.createTypes.create
    }
  }), []);

  return <StyledSaveSearchWizard ref={innerRef} {...innerProps}
                                 steps={steps}
                                 dataKey="search"
                                 initialData={initialData}
                                 onChange={handleChange}
                                 onSubmit={handleSubmit} />
});

SaveSearchWizard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  tableProvider: PropTypes.object,
  saveTypes: PropTypes.array,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func
};

export default SaveSearchWizard;
